// Minimal markdown renderer for body_md fields
// Supports: **bold**, *italic*, `code`, [links](url), \n paragraphs, - lists

export function renderMarkdown(md: string): JSX.Element[] {
  const paragraphs = md.split(/\n\n+/);
  return paragraphs.map((p, i) => {
    const trimmed = p.trim();
    if (!trimmed) return <span key={i} />;

    // Unordered list
    if (/^[-*]\s/.test(trimmed)) {
      const items = trimmed.split(/\n/).filter(l => /^[-*]\s/.test(l.trim()));
      return (
        <ul key={i} className="mb-4 pl-5 text-sm text-proposal-fg2 leading-[1.7]">
          {items.map((item, j) => (
            <li key={j} className="mb-1">{inlineMarkdown(item.replace(/^[-*]\s+/, ''))}</li>
          ))}
        </ul>
      );
    }

    // Headings
    if (trimmed.startsWith('### ')) {
      return <h4 key={i} className="text-sm font-semibold text-proposal-fg1 mt-4 mb-2">{inlineMarkdown(trimmed.slice(4))}</h4>;
    }
    if (trimmed.startsWith('## ')) {
      return <h3 key={i} className="text-base font-semibold text-proposal-fg1 mt-6 mb-3">{inlineMarkdown(trimmed.slice(3))}</h3>;
    }

    // Paragraph
    return <p key={i} className="text-sm text-proposal-fg2 leading-[1.7] mb-3">{inlineMarkdown(trimmed)}</p>;
  });
}

function inlineMarkdown(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  // Process inline patterns: **bold**, *italic*, `code`, [text](url)
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      parts.push(<strong key={match.index} className="text-proposal-fg1">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*') && token.endsWith('*')) {
      parts.push(<em key={match.index}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      parts.push(<code key={match.index} className="font-proposal-mono text-[12px] bg-proposal-bg3 px-1 py-0.5 rounded">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        parts.push(<a key={match.index} href={linkMatch[2]} className="text-proposal-accent underline" target="_blank" rel="noopener noreferrer">{linkMatch[1]}</a>);
      }
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : [text];
}
